/**
 * SMS Segment Calculator Utility
 * 
 * Calculates SMS segment count based on GSM-7 character set and Unicode characters
 * Provides cost estimation and character limit guidance
 */

// GSM-7 character set (160 characters per segment)
const GSM7_CHARS = "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ`¿abcdefghijklmnopqrstuvwxyzäöñüà";

// Extended GSM-7 characters (require escape sequence, count as 2 characters)
const GSM7_EXTENDED = "^{}\\~[]|€";

/**
 * Calculate SMS segments for a message
 * @param {string} message - The SMS message text
 * @returns {object} Segment calculation results
 */
export const calculateSMSSegments = (message) => {
  if (!message || message.length === 0) {
    return {
      segments: 0,
      charactersUsed: 0,
      charactersRemaining: 160,
      encoding: 'GSM-7',
      maxCharsPerSegment: 160,
      isMultiSegment: false,
      estimatedCost: 0,
      warnings: []
    };
  }

  let isGSM7 = true;
  let characterCount = 0;
  const warnings = [];

  // Check each character for encoding type
  for (let i = 0; i < message.length; i++) {
    const char = message.charAt(i);
    
    if (GSM7_CHARS.includes(char)) {
      characterCount += 1;
    } else if (GSM7_EXTENDED.includes(char)) {
      characterCount += 2; // Extended characters count as 2
      warnings.push(`Character "${char}" uses 2 characters (extended GSM-7)`);
    } else {
      isGSM7 = false;
      break;
    }
  }

  let segments;
  let maxCharsPerSegment;
  let encoding;

  if (isGSM7) {
    encoding = 'GSM-7';
    if (characterCount <= 160) {
      segments = 1;
      maxCharsPerSegment = 160;
    } else {
      // Multi-segment GSM-7 messages: 153 characters per segment
      segments = Math.ceil(characterCount / 153);
      maxCharsPerSegment = 153;
    }
  } else {
    // Unicode (UCS-2) encoding
    encoding = 'Unicode';
    characterCount = message.length;
    if (characterCount <= 70) {
      segments = 1;
      maxCharsPerSegment = 70;
    } else {
      // Multi-segment Unicode messages: 67 characters per segment
      segments = Math.ceil(characterCount / 67);
      maxCharsPerSegment = 67;
    }
    warnings.push('Unicode characters detected - reduces character limit');
  }

  const isMultiSegment = segments > 1;
  const currentSegmentUsed = characterCount % maxCharsPerSegment || (characterCount > 0 ? maxCharsPerSegment : 0);
  const charactersRemaining = maxCharsPerSegment - currentSegmentUsed;

  // Cost estimation (typical SMS pricing: $0.02-0.05 per segment)
  const estimatedCost = segments * 0.025; // $0.025 per segment

  // Add warnings for multi-segment
  if (isMultiSegment) {
    warnings.push(`Message will be sent as ${segments} segments`);
    warnings.push(`Cost: ~$${estimatedCost.toFixed(3)} per recipient`);
  }

  return {
    segments,
    charactersUsed: characterCount,
    charactersRemaining: isMultiSegment ? charactersRemaining : (160 - characterCount),
    encoding,
    maxCharsPerSegment,
    isMultiSegment,
    estimatedCost,
    warnings,
    breakdown: {
      totalCharacters: message.length,
      effectiveCharacters: characterCount,
      segmentLimit: isMultiSegment ? maxCharsPerSegment : (encoding === 'GSM-7' ? 160 : 70)
    }
  };
};

/**
 * Format SMS segment information for display
 * @param {object} segmentInfo - Result from calculateSMSSegments
 * @returns {string} Formatted display text
 */
export const formatSegmentInfo = (segmentInfo) => {
  const { segments, charactersUsed, encoding, isMultiSegment, estimatedCost } = segmentInfo;
  
  if (segments === 0) {
    return 'Enter message to see segment info';
  }
  
  let info = `${charactersUsed} chars, ${segments} segment${segments > 1 ? 's' : ''} (${encoding})`;
  
  if (isMultiSegment) {
    info += ` • ~$${estimatedCost.toFixed(3)}/recipient`;
  }
  
  return info;
};

/**
 * Get color indicator for segment count
 * @param {number} segments - Number of segments
 * @returns {string} CSS color value
 */
export const getSegmentColor = (segments) => {
  if (segments <= 1) return '#52c41a'; // Green
  if (segments <= 2) return '#faad14'; // Orange  
  if (segments <= 4) return '#fa8c16'; // Dark orange
  return '#ff4d4f'; // Red
};

export default {
  calculateSMSSegments,
  formatSegmentInfo,
  getSegmentColor
}; 